import { useEffect, useState } from "react";
import { Pill, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

export default function MedHistory({ patientId }) {
    const [rows, setRows] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/patients/${patientId}/med-history`);
                setRows(data);
            } catch (e) { toast.error(fmtErr(e)); }
        })();
    }, [patientId]);

    return (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="med-history-card">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Pill className="h-4 w-4 text-[#E06D1F]" />
                <h3 className="font-heading font-semibold text-slate-900">Medication Reconciliation</h3>
                <span className="text-xs text-slate-500">{rows ? `${rows.length} unique medicine${rows.length === 1 ? "" : "s"}` : ""}</span>
            </div>
            {!rows ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>
            ) : rows.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="med-history-empty">No medicines prescribed yet. Every prescription across all visits is reconciled here automatically.</p>
            ) : (
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                        <th className="px-4 py-2.5">Medicine</th><th className="px-4 py-2.5">Times Prescribed</th><th className="px-4 py-2.5">Last Prescribed</th><th className="px-4 py-2.5 hidden md:table-cell">Last Regimen</th><th className="px-4 py-2.5 hidden lg:table-cell">Prescriber</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} data-testid={`med-history-row-${i}`} className="border-t border-slate-100">
                                <td className="px-4 py-2.5">
                                    <p className="font-medium text-slate-900">{r.name}</p>
                                    {r.generic && <p className="text-xs text-slate-400">{r.generic}</p>}
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${r.count > 2 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`med-count-${i}`}>{r.count}x{r.count > 2 ? " chronic" : ""}</span>
                                </td>
                                <td className="px-4 py-2.5 text-slate-700">{r.last_date || "—"}</td>
                                <td className="px-4 py-2.5 hidden md:table-cell text-xs text-slate-600">{[r.last_frequency, r.last_timing, r.last_duration].filter(Boolean).join(" · ") || "—"}</td>
                                <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-600">{r.prescriber || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
